import React from "react";
import "./NotificationPanel.scss";
import {
  theme,
  Drawer,
  Typography,
  Button,
  Switch,
  Spin,
  Pagination,
} from "antd";
import {
  CloseOutlined,
  CheckOutlined,
  SyncOutlined,
  MessageOutlined,
  BellOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import classNames from "classnames";
import useWindowWidth from "../../hooks/useWindowWidth";

dayjs.extend(relativeTime);

export const EMPTY_TITLE = "No notifications yet";
export const EMPTY_HINT =
  "Order updates and replies from the store will show up here.";
export const EMPTY_UNREAD_TITLE = "You're all caught up";
export const EMPTY_UNREAD_HINT = "There is nothing unread right now.";

const getIcon = (type) => {
  switch (type) {
    case "order_status":
      return <SyncOutlined />;
    case "order_message":
      return <MessageOutlined />;
    default:
      return <BellOutlined />;
  }
};

const formatWhen = (iso) => {
  if (!iso) return "";
  const date = dayjs(iso);
  return date.isValid() ? date.fromNow() : "";
};

// "3 unread messages, 1 status" under the title, from the feed-wide
// counters (never the page).
const describeUnread = (unreadByType) => {
  const parts = [];
  const messages = unreadByType?.order_message || 0;
  const statuses = unreadByType?.order_status || 0;
  if (messages) {
    parts.push(`${messages} unread message${messages === 1 ? "" : "s"}`);
  }
  if (statuses) {
    parts.push(`${statuses} status update${statuses === 1 ? "" : "s"}`);
  }
  return parts.join(", ");
};

// The bell's drawer. Same width and header rule as the order chat drawer,
// full width on phones. Purely presentational: every handler comes from
// useNotifications.
export default function NotificationPanel({
  open,
  onClose,
  notifications,
  loading,
  totalCount,
  perPage,
  currentPage,
  onPageChange,
  onNotificationClick,
  onMarkAllRead,
  markAllLoading,
  unreadCount,
  unreadByType,
  onlyUnread,
  onToggleUnread,
}) {
  const { useToken } = theme;
  const { token } = useToken();
  const { isMobile } = useWindowWidth();
  const subtitle = describeUnread(unreadByType);
  const hasItems = Boolean(notifications?.length);

  return (
    <Drawer
      className="notificationPanel-cont"
      open={open}
      destroyOnHidden={true}
      placement="right"
      size={isMobile ? "100%" : 560}
      style={{
        backgroundColor: token?.Drawer?.colorBgDrawer,
      }}
      styles={{
        header: {
          flex: "0 0 auto",
          height: isMobile ? 56 : 80,
          minHeight: isMobile ? 56 : 80,
          boxSizing: "border-box",
          borderBottom: `1px solid ${token?.colorPrimary}`,
        },
        body: {
          padding: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        },
      }}
      title={
        <div className="notificationPanel-cont__titleBar">
          <div className="notificationPanel-cont__titleBar__text">
            <Typography.Text
              className="notificationPanel-cont__title"
              style={{
                color: token?.Typography?.mainColorText,
              }}>
              Notifications
            </Typography.Text>
            {subtitle ? (
              <Typography.Text className="notificationPanel-cont__subtitle">
                {subtitle}
              </Typography.Text>
            ) : null}
          </div>
          {unreadCount > 0 ? (
            <Button
              type="link"
              size="small"
              className="notificationPanel-cont__markAllBtn"
              icon={<CheckOutlined />}
              loading={markAllLoading}
              onClick={onMarkAllRead}
              style={{
                color: token?.Typography?.colorPrimaryText,
              }}>
              Mark all as read
            </Button>
          ) : null}
        </div>
      }
      closeIcon={
        <CloseOutlined
          style={{
            color: token?.icons?.whiteIcon,
            fontSize: "20px",
          }}
        />
      }
      onClose={onClose}>
      <div
        className="notificationPanel-cont__filter"
        style={{
          backgroundColor: token?.Card?.colorBgCard,
        }}>
        <Switch
          size="small"
          checked={Boolean(onlyUnread)}
          onChange={onToggleUnread}
          aria-label="Only show unread"
        />
        <Typography.Text
          className="notificationPanel-cont__filter__label"
          style={{
            color: token?.Typography?.mainColorText,
          }}>
          Only show unread
        </Typography.Text>
      </div>

      <div className="notificationPanel-cont__body">
        {loading && !hasItems ? (
          <div className="notificationPanel-cont__spinWrapper">
            <Spin size="large" />
          </div>
        ) : !hasItems ? (
          <div className="notificationPanel-cont__empty">
            <BellOutlined className="notificationPanel-cont__empty__icon" />
            <Typography.Text
              className="notificationPanel-cont__empty__title"
              style={{
                color: token?.Typography?.mainColorText,
              }}>
              {onlyUnread ? EMPTY_UNREAD_TITLE : EMPTY_TITLE}
            </Typography.Text>
            <Typography.Text className="notificationPanel-cont__empty__hint">
              {onlyUnread ? EMPTY_UNREAD_HINT : EMPTY_HINT}
            </Typography.Text>
          </div>
        ) : (
          <Spin spinning={Boolean(loading)}>
            <div className="notificationPanel-cont__list" role="list">
              {notifications.map((notif) => {
                const unread = !notif?.is_read;
                return (
                  <div
                    key={notif?.id}
                    role="listitem"
                    className={classNames("notificationPanel-cont__item", {
                      "notificationPanel-cont__item--unread": unread,
                    })}
                    style={{
                      backgroundColor: unread
                        ? "color-mix(in srgb, var(--primary-color, #61bc18) 12%, transparent)"
                        : token?.Card?.colorBgCard,
                      borderColor: unread
                        ? "color-mix(in srgb, var(--primary-color, #61bc18) 35%, transparent)"
                        : "color-mix(in srgb, var(--text-main, #fff) 8%, transparent)",
                    }}>
                    <button
                      type="button"
                      className="notificationPanel-cont__item__btn"
                      aria-label={notif?.title || "Notification"}
                      onClick={() => {
                        onNotificationClick?.(notif);
                      }}>
                      <span
                        className={classNames(
                          "notificationPanel-cont__item__icon",
                          {
                            "notificationPanel-cont__item__icon--unread":
                              unread,
                          }
                        )}
                        style={
                          unread ? { color: token?.colorPrimary } : undefined
                        }>
                        {getIcon(notif?.type)}
                      </span>
                      <span className="notificationPanel-cont__item__content">
                        <span className="notificationPanel-cont__item__header">
                          <Typography.Text
                            className="notificationPanel-cont__item__title"
                            style={{
                              color: token?.Typography?.mainColorText,
                            }}
                            ellipsis={{
                              tooltip: { title: notif?.title || "" },
                            }}>
                            {notif?.title || ""}
                          </Typography.Text>
                          {unread ? (
                            <span
                              className="notificationPanel-cont__item__dot"
                              aria-hidden="true"
                              style={{
                                backgroundColor: token?.colorPrimary,
                              }}
                            />
                          ) : null}
                        </span>
                        {notif?.type === "order_message" && notif?.body ? (
                          <Typography.Text className="notificationPanel-cont__item__body">
                            {notif.body}
                          </Typography.Text>
                        ) : null}
                        <span className="notificationPanel-cont__item__meta">
                          {notif?.order_number ? (
                            <Typography.Text className="notificationPanel-cont__item__meta__order">
                              #{notif.order_number}
                            </Typography.Text>
                          ) : null}
                          <Typography.Text className="notificationPanel-cont__item__meta__time">
                            {formatWhen(notif?.created_at)}
                          </Typography.Text>
                        </span>
                      </span>
                    </button>
                  </div>
                );
              })}
            </div>
          </Spin>
        )}
      </div>

      {totalCount > perPage ? (
        <div
          className="notificationPanel-cont__footer"
          style={{
            backgroundColor: token?.Card?.colorBgCard,
          }}>
          <Pagination
            size="small"
            current={currentPage}
            pageSize={perPage}
            total={totalCount}
            onChange={onPageChange}
            disabled={Boolean(loading)}
            showSizeChanger={false}
          />
        </div>
      ) : null}
    </Drawer>
  );
}
